// Central authenticated fetch wrapper.
// Adds global 401 handling: emits a single 'app:logout' event and prevents repeat storms.
// Intent: unify backend-driven session invalidation UX (expired session, role revoked, force logout).
// Also auto-injects CSRF tokens for state-changing requests so that legacy callsites stay protected.
// Includes request deduplication for GET requests to prevent 429 rate limiting.

import { ensureCsrfToken } from "../csrf";
import { toApiUrl } from "../config/api";

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS", "TRACE"]);

let logoutEmitted = false;
let principalEpoch = 0;

// ============ Request Deduplication for GET requests ============
// Prevents multiple simultaneous identical GET requests (e.g., /sales/api/pipeline)
// that cause 429 rate limit errors when React components mount rapidly

interface InflightRequest {
  promise: Promise<Response>;
}

// Map of URL -> inflight request promise
const inflightRequests = new Map<string, InflightRequest>();

function principalChangedAbortError(): Error {
  if (typeof DOMException !== 'undefined') {
    return new DOMException('Authenticated principal changed before request completion', 'AbortError');
  }
  const error = new Error('Authenticated principal changed before request completion');
  error.name = 'AbortError';
  return error;
}

function assertPrincipalEpoch(capturedEpoch: number): void {
  if (capturedEpoch !== principalEpoch) throw principalChangedAbortError();
}

type ResponseBodyMethod = 'arrayBuffer' | 'blob' | 'bytes' | 'formData' | 'json' | 'text';

const RESPONSE_BODY_METHODS = new Set<PropertyKey>([
  'arrayBuffer',
  'blob',
  'bytes',
  'formData',
  'json',
  'text',
]);

function guardResponseBody(
  body: ReadableStream<Uint8Array>,
  capturedEpoch: number,
): ReadableStream<Uint8Array> {
  let sourceReader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  const reader = () => {
    assertPrincipalEpoch(capturedEpoch);
    sourceReader ??= body.getReader();
    return sourceReader;
  };

  return new ReadableStream<Uint8Array>({
    async pull(controller) {
      try {
        assertPrincipalEpoch(capturedEpoch);
        const chunk = await reader().read();
        assertPrincipalEpoch(capturedEpoch);
        if (chunk.done) {
          sourceReader?.releaseLock();
          sourceReader = null;
          controller.close();
          return;
        }
        controller.enqueue(chunk.value);
      } catch (error) {
        try {
          await sourceReader?.cancel(error);
        } catch {
          // The principal ownership error remains authoritative.
        }
        sourceReader = null;
        controller.error(error);
      }
    },
    async cancel(reason) {
      try {
        await (sourceReader ? sourceReader.cancel(reason) : body.cancel(reason));
      } finally {
        sourceReader = null;
      }
    },
  }, { highWaterMark: 0 });
}

function guardAuthenticatedResponse(response: Response, capturedEpoch: number): Response {
  let guardedBody: ReadableStream<Uint8Array> | null | undefined;
  return new Proxy(response, {
    get(target, property) {
      if (property === 'body') {
        assertPrincipalEpoch(capturedEpoch);
        if (guardedBody === undefined) {
          guardedBody = target.body
            ? guardResponseBody(target.body, capturedEpoch)
            : null;
        }
        return guardedBody;
      }

      if (property === 'clone') {
        return () => {
          assertPrincipalEpoch(capturedEpoch);
          const clone = target.clone();
          assertPrincipalEpoch(capturedEpoch);
          return guardAuthenticatedResponse(clone, capturedEpoch);
        };
      }

      if (RESPONSE_BODY_METHODS.has(property)) {
        const method = target[property as ResponseBodyMethod].bind(target) as () => Promise<unknown>;
        return async () => {
          assertPrincipalEpoch(capturedEpoch);
          try {
            const value = await method();
            assertPrincipalEpoch(capturedEpoch);
            return value;
          } catch (error) {
            assertPrincipalEpoch(capturedEpoch);
            throw error;
          }
        };
      }

      const value = Reflect.get(target, property, target) as unknown;
      return typeof value === 'function'
        ? (value as (...args: unknown[]) => unknown).bind(target)
        : value;
    },
  });
}

/**
 * Synchronously invalidates every authenticated request started by the previous
 * principal. In particular, a mutation paused on CSRF hydration must never be
 * dispatched later with the replacement principal's browser cookie.
 */
export function advanceAuthFetchPrincipalEpoch(): void {
  principalEpoch += 1;
  inflightRequests.clear();
  logoutEmitted = false;
}

function shouldDedupe(url: string): boolean {
  return url.includes('/api/') || url.startsWith('/api/');
}

export interface AuthFetchOptions extends RequestInit {
  autoLogout?: boolean; // default true; if false, 401 will NOT emit global logout
  suppressLogoutEvent?: boolean; // alias for autoLogout=false (takes precedence if set)
  skipDedupe?: boolean; // if true, skip request deduplication (for forced refreshes)
}

export async function authFetch(
  input: RequestInfo | URL,
  init?: AuthFetchOptions,
): Promise<Response> {
  const requestPrincipalEpoch = principalEpoch;
  const normalizedInput: RequestInfo | URL =
    typeof input === "string" || input instanceof URL ? toApiUrl(input) : input;
  const auto = init?.suppressLogoutEvent ? false : (init?.autoLogout ?? true);
  // Sensible defaults for API-style requests
  const requestInput = normalizedInput instanceof Request ? normalizedInput : undefined;
  const headers = new Headers(init?.headers ?? requestInput?.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  if (!headers.has("X-Requested-With"))
    headers.set("X-Requested-With", "XMLHttpRequest");
  const method = (init?.method ?? requestInput?.method ?? "GET").toUpperCase();

  // Request deduplication for GET requests to prevent 429 errors
  const urlStr = typeof normalizedInput === "string"
    ? normalizedInput
    : normalizedInput instanceof URL
      ? normalizedInput.toString()
      : normalizedInput.url;
  // Header variants (e.g. locale, authorization or Range) are distinct reads.
  const dedupeKey = JSON.stringify([
    urlStr, [...headers.entries()].sort(([a], [b]) => a.localeCompare(b)),
    init?.credentials ?? requestInput?.credentials ?? 'include',
    init?.cache ?? requestInput?.cache, init?.mode ?? requestInput?.mode,
    init?.redirect ?? requestInput?.redirect, auto,
  ]);
  const isGetRequest = method === "GET";
  // Independently cancellable requests must not inherit another consumer's
  // AbortSignal. React Query already shares requests within the same query.
  const skipDedupe = Boolean(init?.skipDedupe || init?.signal || requestInput);

  if (isGetRequest && !skipDedupe && shouldDedupe(urlStr)) {
    // Check for existing inflight request with same URL
    const existing = inflightRequests.get(dedupeKey);
    if (existing) {
      // Return a clone of the existing promise's response
      try {
        const response = await existing.promise;
        assertPrincipalEpoch(requestPrincipalEpoch);
        // Clone the response so each caller gets their own readable body
        return response.clone();
      } catch (error) {
        assertPrincipalEpoch(requestPrincipalEpoch);
        // Share failures too; per-query retry policy owns the next attempt.
        throw error;
      }
    }
  }

  if (!SAFE_METHODS.has(method) && !headers.has("X-CSRF-Token")) {
    const token = await ensureCsrfToken();
    assertPrincipalEpoch(requestPrincipalEpoch);
    if (token) {
      headers.set("X-CSRF-Token", token);
    }
  }

  const opts: RequestInit = {
    credentials: init?.credentials ?? "include",
    ...init,
    headers,
  };

  // Create the fetch promise
  const fetchPromise = (async (): Promise<Response> => {
    assertPrincipalEpoch(requestPrincipalEpoch);
    const res = await fetch(normalizedInput, opts);
    assertPrincipalEpoch(requestPrincipalEpoch);
    if (res.ok && !SAFE_METHODS.has(method)) {
      // Reads started before this write may still be in flight. New refreshes
      // must not join those pre-write snapshots.
      inflightRequests.clear();
    }

    // If the cached CSRF token became stale (e.g. after login/role change), refresh and retry once.
    if (res.status === 403 && !SAFE_METHODS.has(method)) {
      const body = init?.body as unknown;
      const retryableBody =
        body == null ||
        typeof body === "string" ||
        body instanceof FormData ||
        body instanceof URLSearchParams ||
        body instanceof Blob;

      if (retryableBody) {
        try {
          const ct = (res.headers.get("content-type") || "").toLowerCase();
          if (ct.includes("application/json")) {
            const j = await res
              .clone()
              .json()
              .catch(() => null);
            const code = (j && j.error && j.error.code) as string | undefined;
            if (code === "csrf_validation_failed") {
              const fresh = await ensureCsrfToken(true);
              assertPrincipalEpoch(requestPrincipalEpoch);
              if (fresh) {
                const retryHeaders = new Headers(headers);
                retryHeaders.set("X-CSRF-Token", fresh);
                const retryOpts: RequestInit = {
                  ...opts,
                  headers: retryHeaders,
                };
                const retryResponse = await fetch(normalizedInput, retryOpts);
                assertPrincipalEpoch(requestPrincipalEpoch);
                if (retryResponse.ok) inflightRequests.clear();
                return guardAuthenticatedResponse(retryResponse, requestPrincipalEpoch);
              }
            }
          }
        } catch {
          assertPrincipalEpoch(requestPrincipalEpoch);
          // If retry logic fails, fall back to the original response
        }
      }
    }

    if (res.status === 401 && auto) {
      if (!logoutEmitted) {
        logoutEmitted = true;
        try {
          window.dispatchEvent(
            new CustomEvent("app:logout", {
              detail: { reason: "unauthorized", url: urlStr },
            }),
          );
        } catch {
          /* ignore */
        }
      }
    }
    return guardAuthenticatedResponse(res, requestPrincipalEpoch);
  })();

  // Store the promise for deduplication (only for GET requests that should be deduped)
  if (isGetRequest && !skipDedupe && shouldDedupe(urlStr)) {
    const clonePromise = fetchPromise.then((res) => res.clone());
    // Prevent "Uncaught (in promise)" when the request is aborted while
    // the dedup entry sits un-awaited in the map.  Attaching a no-op
    // handler marks the promise as "handled" without swallowing the error
    // for callers who later `await` it via the dedup path.
    clonePromise.catch(() => {});

    const entry = {
      promise: clonePromise,
    };
    inflightRequests.set(dedupeKey, entry);

    // Clean up after request completes
    clonePromise.finally(() => {
      // A settled response is not a cache: a post-mutation refresh must read
      // current data. Identity also prevents an older request deleting a newer one.
      if (inflightRequests.get(dedupeKey) === entry) {
        inflightRequests.delete(dedupeKey);
      }
    }).catch(() => {}); // suppress unhandled rejection on this branch
  }

  return fetchPromise;
}

export function resetAuthFetchLogoutFlag(): void {
  logoutEmitted = false;
}
